import { useState, useEffect, useCallback } from 'react';
import { Lexer, Parser, Generator } from 'jertscript2';
import Editor from './components/Editor';
import Output from './components/Output';
import Examples from './components/Examples';
import Share, { getCodeFromUrl, clearCodeFromUrl } from './components/Share';
import { Logo } from './components/Logo';
import { Footer } from './components/Footer';
import './App.css';

const DEFAULT_CODE = `// Bienvenue dans Jertscript v2! 🥐
// Now with TypeScript types!

croissant message: texte = "Bonjour, le monde!";
dire(message);

// Variables with types
baguette count: nombre = 0;
pendant (count < 3) {
  dire("Vive la France!");
  count = count + 1;
}

// Typed function
fonction add(a: nombre, b: nombre): nombre {
  retourner a + b;
}

dire(add(2, 3));
`;

interface TranspileResult {
  code: string;
  error: string | null;
}

function transpile(source: string): TranspileResult {
  try {
    const lexer = new Lexer(source);
    const tokens = lexer.tokenize();

    const parser = new Parser(tokens);
    const ast = parser.parse();

    const generator = new Generator();
    const output = generator.generate(ast);

    return { code: output, error: null };
  } catch (err) {
    const error = err instanceof Error ? err.message : String(err);
    return { code: '', error };
  }
}

function App() {
  const [code, setCode] = useState<string>(() => {
    const urlCode = getCodeFromUrl();
    if (urlCode) {
      clearCodeFromUrl();
      return urlCode;
    }
    return DEFAULT_CODE;
  });

  const [output, setOutput] = useState<TranspileResult>({ code: '', error: null });

  // Transpile on code change
  useEffect(() => {
    const result = transpile(code);
    setOutput(result);
  }, [code]);

  const handleCodeChange = useCallback((newCode: string) => {
    setCode(newCode);
  }, []);

  const handleExampleSelect = useCallback((exampleCode: string) => {
    setCode(exampleCode);
  }, []);

  return (
    <div className="app">
      <div className="app-frame">
        {/* Header */}
        <header className="header">
          <div className="header-content">
            <Logo />
            <Share code={code} />
          </div>
        </header>

        {/* Main Panels */}
        <main className="main">
          {/* Editor Panel */}
          <div className="editor-panel">
            <div className="panel-header">
              <span>Jertscript v2</span>
              <Examples onSelect={handleExampleSelect} />
            </div>
            <Editor
              code={code}
              onChange={handleCodeChange}
            />
          </div>

          {/* Output Panel */}
          <div className="output-panel">
            <Output
              typescript={output.code}
              error={output.error}
            />
          </div>
        </main>

        <Footer />
      </div>
    </div>
  );
}

export default App;
